import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Avatar, Flex, Input, Select, useToast } from '@chakra-ui/react';
import { ProfessoresGet } from '../Professores';
import { listarBooks, updateAula } from '../../services/api';
import { CampoForm } from '../../components/CampoForm';
import { DrawerForm } from '../../components/DrawerForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { vazio } from '../../utils/validacao';
import { AlunosType } from '.';
import { BookGet } from '../Books';

export type AulaDrawerProps = {
	id: string;
	alunoId: string;
	materialId: string;
	titulo: string;
	professorId: string;
	data: string;
	status: string;
};

type DrawerCreateProps = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	afterClose: () => void;
	professor: ProfessoresGet;
	aluno: AlunosType;
	aula: AulaDrawerProps;
};

type Campo = 'titulo' | 'data' | 'materialId';

export function DrawerUpdate({ isOpen, onClose, afterClose, professor, aluno, aula }: DrawerCreateProps) {
	const toast = useToast();
	const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

	const [ loading, setLoading ] = useState(false);

	const [ titulo, setTitulo ] = useState<string>(aula.titulo);
	const [ data, setData ] = useState<string>(aula.data);
	const [ materialId, setMaterialId ] = useState<string>(aula.materialId);
	const [ books, setBooks ] = useState<BookGet[]>([]);

	const buscarMateriais = useCallback(
		() => {
			listarBooks()
				.then((response) => {
					setBooks(response.data);
				})
				.catch((error) => {
					if (!error.response) return;
					toast({
						title: `Não foi possível listar os materiais.`,
						status: 'error',
						isClosable: true
					});
				});
		},
		[ toast ]
	);

	// Descarta as edicoes voltando aos dados da aula, e nao para campos vazios: o
	// pai reaproveita o mesmo objeto ao reabrir a mesma linha.
	const resetForm = useCallback(
		() => {
			setData(aula.data);
			setTitulo(aula.titulo);
			setMaterialId(aula.materialId);
			limparTudo();
		},
		[ aula, limparTudo ]
	);

	useEffect(
		() => {
			if (!isOpen) return;
			buscarMateriais();
			resetForm();
		},
		[ isOpen, buscarMateriais, resetForm ]
	);

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(titulo)) encontrados.titulo = 'Título é obrigatório!';
		if (vazio(data)) encontrados.data = 'Data é obrigatória!';
		if (!materialId) encontrados.materialId = 'Selecione o material!';

		return encontrados;
	};

	const handleSalvar = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		setLoading(true);
		updateAula(aula.id, {
			data,
			alunoId: aluno.id,
			professorId: professor.id,
			status: 'AGENDADA',
			titulo: titulo.trim(),
			materialId
		})
			.then(() => {
				setLoading(false);
				toast({
					title: `Aula atualizada com sucesso.`,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				if (!error.response) return;
				const message = error.response.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível atualizar a aula.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleFechar = () => {
		resetForm();
		onClose();
		afterClose();
	};

	return (
		<DrawerForm
			isOpen={isOpen}
			onClose={handleFechar}
			titulo="Editar Aula"
			formId="form-editar-aula"
			loading={loading}
			onSubmit={handleSalvar}
		>
			<CampoForm label="Professor" isRequired={false}>
				<Flex
					gap={'10px'}
					alignItems="center"
					justifyContent="center"
					paddingTop="10px"
					paddingBottom="10px"
					borderWidth="1px"
					borderColor="gray.200"
					borderRadius="10px"
				>
					<Avatar size="sm" name={professor.nome} src={professor.user.avatarUrl} />
					{`${professor.nome} ${professor.sobrenome}`}
				</Flex>
			</CampoForm>

			<CampoForm label="Aluno" isRequired={false}>
				<Flex
					gap={'10px'}
					alignItems="center"
					justifyContent="center"
					paddingTop="10px"
					paddingBottom="10px"
					borderWidth="1px"
					borderColor="gray.200"
					borderRadius="10px"
				>
					<Avatar size="sm" name={aluno.nome} src={aluno.user.avatarUrl} />
					{`${aluno.nome} ${aluno.sobrenome}`}
				</Flex>
			</CampoForm>

			<CampoForm label="Título" htmlFor="titulo" erro={erros.titulo}>
				<Input
					id="titulo"
					placeholder="Insira o Título"
					onChange={(e) => {
						setTitulo(e.target.value);
						limparErro('titulo');
					}}
					value={titulo}
				/>
			</CampoForm>

			<CampoForm label="Data" htmlFor="data" erro={erros.data}>
				<Input
					id="data"
					type="datetime-local"
					onChange={(e) => {
						setData(e.target.value);
						limparErro('data');
					}}
					value={data}
				/>
			</CampoForm>

			<CampoForm label="Material" htmlFor="material" erro={erros.materialId}>
				<Select
					id="material"
					placeholder="Selecione o Material"
					onChange={(e) => {
						setMaterialId(e.target.value);
						limparErro('materialId');
					}}
					value={materialId}
				>
					{books.map((item) => (
						<option value={item.id} key={item.id} style={{ color: 'black' }}>
							{item.nome}
						</option>
					))}
				</Select>
			</CampoForm>
		</DrawerForm>
	);
}
